import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { buttonVariants } from '@playcastdotio/Shadcn';
import { useState, useEffect } from 'react';
import { sendMessage } from '../../api/modules/realtimeMessaging';
import { websocket, MatchmakingMessageType } from '@playcastdotio/Messaging';
import { useAuth } from '@clerk/clerk-react';
import { authStore } from '../../api/stores/globalStores';
import { sendAnalytics, PlaycastAnalyticsHostSoftKicked } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../../api';

export const SoftKickHost = () => {
  const [isActivating, setIsActivating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { isSignedIn, isLoaded } = useAuth();
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Extract session ID from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const session = urlParams.get('session');
    setSessionId(session);
    console.log('[SoftKickHost] Session ID from URL:', session);
  }, []);

  // Debug connection state
  useEffect(() => {
  }, [isSignedIn, isLoaded, sessionId]);

  const handleActivateSoftKick = async () => {
    
    setIsActivating(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      console.log('[SoftKickHost] Creating soft kick message...');
      
      let softKickMessage;
      if (sessionId) {
        // Use session ID-based soft kick
        softKickMessage = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_ACTIVATE_SOFT_KICK_BY_SESSION_ID, {
          hostSessionId: sessionId,
          token: '', // Not needed for this flow
        });
        console.log('[SoftKickHost] Using session ID-based soft kick');
      } 

      sendAnalytics({
        name: 'Host Soft Kicked',
        role: 'host',
        persistentId: getPersistentPlayerId(),
        hostAuthId: authStore.currentState.playerAuthId,
        tier2Filters: ['machine'],
      } as PlaycastAnalyticsHostSoftKicked);
      
      sendMessage(softKickMessage);
      
      console.log('[SoftKickHost] Message sent successfully');
      setSuccessMessage('Soft kick activated! All players will be removed from your machine in 5 minutes.');
    } catch (error) {
      console.error('[SoftKickHost] Failed to activate soft kick:', error);
      setErrorMessage('Failed to activate soft kick. Please try again.');
    } finally {
      console.log('[SoftKickHost] Activation process completed');
      setIsActivating(false);
    }
  };

  return (
    <div className='flex flex-col items-center justify-center gap-y-8 min-h-screen bg-gray-900 text-white p-8'>
      <div className='grid-rows col-span-full mx-auto grid content-center justify-center gap-y-4 text-center lg:gap-y-8 max-w-2xl'>
        <div className='flex items-center justify-center gap-4 mb-8'>
          <img src='/images/playcast_logo-96x96.png' alt='Playcast Logo' className='h-16 w-16' />
          <h1 className='text-4xl font-bold'>Playcast Host Control</h1>
        </div>
        
        <h2 className='text-2xl font-bold text-yellow-400'>
          Your Machine is in Host Mode
        </h2>
        
        {/* Session Status Info */}
        <div className='p-4 rounded-lg'>
          <p className='text-blue-400 font-semibold'>Remote Control Active</p>
          <p className='text-sm text-gray-300'>
            This page allows you to control your machine remotely.
          </p>
          {sessionId ? (
            <div className='text-xs text-green-400 mt-2'>
              ✅ Session ID found: {sessionId.substring(0, 8)}...
              <br />
              Soft kick will target this specific machine.
            </div>
          ) : (
            <div className='text-xs text-yellow-400 mt-2'>
              ⚠️ No session ID found in URL.
              <br />
              Soft kick will target the first available machine.
            </div>
          )}
        </div>
        
        <p className='text-lg text-gray-300'>
          Your machine is currently in the matchmaking pool and may have active gaming sessions. 
          Use the controls below to manage your machine.
        </p>
        
        {/* Status Messages */}
        {successMessage && (
          <div className='text-green-400 text-sm bg-green-900/20 p-4 rounded-lg'>
            {successMessage}
          </div>
        )}
        
        {errorMessage && (
          <div className='text-red-400 text-sm bg-red-900/20 p-4 rounded-lg'>
            {errorMessage}
          </div>
        )}
        
        {/* Action Buttons */}
        <div className='flex flex-col gap-4 w-full max-w-md mx-auto'>
          {/* Soft Kick Button */}
          <div className='bg-red-900/20 p-4 rounded-lg border border-red-400'>
            <div className='space-y-2'>
              <button
                onClick={handleActivateSoftKick}
                disabled={isActivating}
                className={clsx(
                  buttonVariants({ variant: 'destructive' }),
                  'w-full py-6 text-lg font-semibold',
                  isActivating && 'opacity-50 cursor-not-allowed'
                )}
                data-testid='activate-soft-kick-button'
              >
                {isActivating ? 'Activating...' : '🚨 Activate Soft Kick 🚨'}
              </button>
            </div>
          </div>
        </div>
        
        {/* Warning Message */}
        <div className='max-w-md text-sm text-yellow-400 p-4 rounded-lg mx-auto text-center'>
          <p className='font-semibold mb-2'>Important</p>
          <p>
            • Soft kick will remove all players from your machine after a 5-minute warning period and then exit host mode<br/>
            • This action cannot be undone<br/>
            • If no players are connected, the soft kick will immediately exit host mode
          </p>
        </div>
      </div>
    </div>
  );
}; 
